/**
 * pdfChassis.js — the primitives every Osteo3d jsPDF document shares.
 *
 * Extracted from sseReport.js and illustrationDoc.js when the Case Report
 * Builder became a third consumer. Scope is deliberately narrow: only things
 * that were **already byte-identical** in both documents live here. Page
 * headers, footers and info bars do NOT — each document's chrome is measurably
 * different (different margins, different title blocks, different footer text),
 * and pretending otherwise would force one of them to move.
 *
 * Guarded by src/lib/__tests__/pdfGolden.test.js: if anything here changes what
 * either shipped document renders, the hashes fail.
 */

// A4 portrait, millimetres. Both documents use this; only their margins differ.
export const PW = 210
export const PH = 297

/**
 * The report brand palette.
 *
 * ⚠️ illustrationDoc.js deliberately keeps its own GREY (120) and INK (30)
 * rather than importing these (128 / 40). That difference is almost certainly
 * historical drift, not a design decision — but the Packaging Illustration is a
 * printed label, so unifying the two would visibly change a shipped artifact.
 * Left alone on purpose; raise it with the team before touching it.
 */
export const BRAND = {
  CYAN: [30, 160, 200],   // #1ea0c8
  DARK: [13, 46, 62],     // #0d2e3e
  RED: [192, 57, 43],     // #c0392b
  GREY: [128, 128, 128],  // footer text
  LGREY: [205, 209, 214], // light image/box borders (ink-friendly)
  INK: [40, 40, 40],
  RULE: [150, 162, 170],  // soft header info-bar rules
  GRID: [214, 219, 224],  // light table gridlines
  THEAD: [236, 243, 247], // light table header fill, dark text
}

// ── text ─────────────────────────────────────────────────────────────────────

/**
 * jsPDF's default Helvetica is WinAnsi-encoded — a few common non-WinAnsi glyphs
 * (arrows) render as garbage, so map them to ASCII. (×, ³, — are in WinAnsi, so
 * they are left alone.)
 */
export const sanitize = (s) =>
  String(s).replace(/→/g, '->').replace(/←/g, '<-').replace(/[’]/g, "'")

// ── fonts ────────────────────────────────────────────────────────────────────

export async function fetchB64(url) {
  const res = await fetch(url)
  // fetch() does not throw on a 404: an HTML error page would otherwise be handed to
  // jsPDF as a font and fail every face registered after it.
  if (res.ok === false) throw new Error(`${url}: HTTP ${res.status}`)
  const buf = await res.arrayBuffer()
  let bin = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

/**
 * Embed Mulish (Google font) into the doc. Returns the font family name to use
 * throughout — 'Mulish', or 'helvetica' if the TTFs could not be fetched.
 *
 * The caller keeps the returned name; this module holds no font state, so two
 * documents built in the same session cannot affect each other.
 */
/**
 * Mulish is the Osteo3d house face — the same one the website and every other
 * property use, so all three PDFs are set in it. (Questrial was tried for the
 * case report on 2026-08-01 and reverted: consistency across the brand beats
 * matching one artefact, and Questrial ships a single weight with no bold.)
 */
export async function registerMulish(doc, { medium = false } = {}) {
  try {
    const [reg, bold] = await Promise.all([
      fetchB64('/fonts/Mulish-Regular.ttf'),
      fetchB64('/fonts/Mulish-Bold.ttf'),
    ])
    doc.addFileToVFS('Mulish-Regular.ttf', reg)
    doc.addFont('Mulish-Regular.ttf', 'Mulish', 'normal')
    doc.addFileToVFS('Mulish-Bold.ttf', bold)
    doc.addFont('Mulish-Bold.ttf', 'Mulish', 'bold')
    if (medium) {
      // Medium, on request: the case report master's footer small print is set in it
      // (26 Sep 2026). Same family and licence (OFL) as the two above; the file is the
      // monorepo's own copy. Opt-in, so the SSE report's bytes do not change for a face it
      // never uses. Its OWN try: a missing or unreadable Medium must cost the medium weight
      // only, never drop the whole document to helvetica (G3 review, 28 Sep).
      try {
        const med = await fetchB64('/fonts/Mulish-Medium.ttf')
        doc.addFileToVFS('Mulish-Medium.ttf', med)
        doc.addFont('Mulish-Medium.ttf', 'Mulish', 'medium')
      } catch { /* the writer maps 'medium' to normal when the face is absent */ }
    }
    return 'Mulish'
  } catch { return 'helvetica' }
}

// ── watermark ────────────────────────────────────────────────────────────────

/**
 * The tiled faint diagonal "Osteo3d ®" watermark, drawn on every page of every
 * Osteo3d document. Font is a parameter only because the two existing documents
 * pass different ones (sseReport uses Mulish once embedded, illustrationDoc has
 * always used Helvetica); every constant below is shared.
 */
export function tiledWatermark(doc, font = 'helvetica') {
  doc.saveGraphicsState()
  doc.setGState(new doc.GState({ opacity: 0.09 }))
  doc.setTextColor(140, 150, 155)
  doc.setFont(font, 'normal')
  doc.setFontSize(13)
  for (let y = 20; y < PH; y += 26) {
    for (let x = -10; x < PW + 40; x += 55) {
      doc.text('Osteo3d ®', x, y, { angle: 30 })
    }
  }
  doc.restoreGraphicsState()
}

/** Points → millimetres, for turning a font size into a distance on the page. */
const PT_MM = 0.352778

/**
 * The watermark lattice, in page millimetres.
 *
 * Computed in ONE place because two things draw it and they must not drift: the
 * vector tiles on the page (caseReportDoc.drawWatermark) and the copy stamped
 * into each picture (stampWatermark). A tile stamped at a different anchor from
 * the one the page draws beside it reads as a seam at the picture's edge.
 *
 * The phase is the geometry's where it states one — the master's watermark is
 * measured to the tile, not only to the pitch.
 */
export function watermarkLattice(w) {
  // A non-positive pitch loops for ever while pushing — measured, it exhausts
  // the heap. These values live in editable geometry data and are read once per
  // picture inside the draw loop, so a wrong page beats a hung tab mid-export.
  if (!(w.stepX > 0) || !(w.stepY > 0)) return []
  const x0 = w.originX ?? -10
  const y0 = w.originY ?? w.stepY
  const out = []
  for (let y = y0; y < PH + w.stepY; y += w.stepY) {
    for (let x = x0; x < PW + 40; x += w.stepX) out.push({ x, y })
  }
  return out
}

/**
 * A colour as it RENDERS at `opacity` over white.
 *
 * 🔴 The case report PDF carries NO transparency, and this is how the watermark
 * keeps its look without any. Measured 30 Sep 2026 on the file the team printed:
 * the watermark was one alpha object (`/ca .17`) spanning the whole sheet, in a
 * file declaring %PDF-1.3 — a version with no transparency at all. Put through a
 * print path that cannot composite (`pdftops -level2`, which is what an office
 * PostScript or PCL driver does) the page came out as ONE 2480×3507 raster with
 * zero vector strokes: the frame reached the printer as halftoned photo pixels
 * instead of toner-resolution lines. That is why converting to PNG and printing
 * that looked better — the team chose the raster resolution themselves — and why
 * three width changes (0.085 → 0.18 → 0.254 mm) never fixed it. They were
 * chasing a symptom.
 *
 * White is the one backdrop a page-level tile can count on, because every
 * picture now carries its own stamped copy (stampWatermark).
 */
export const overWhite = (color, opacity) =>
  color.map((c) => Math.round(255 - opacity * (255 - c)))

// ── images ───────────────────────────────────────────────────────────────────

export const asDataUrl = (b64) =>
  (b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`)

export function loadImg(dataUrl) {
  return new Promise((resolve) => {
    const im = new Image()
    im.onload = () => resolve(im)
    im.onerror = () => resolve(null)
    im.src = dataUrl
  })
}

/**
 * Convert a render data URL to JPEG to shrink the PDF (~6-10× vs PNG). Renders
 * are photographic and opaque so JPEG is fine; the watermark and footer are PDF
 * vectors drawn separately, so they stay crisp.
 *
 * @returns {Promise<{url: string, ar: number}>} JPEG data URL + aspect ratio
 */
export function toJpeg(dataUrl, quality = 0.85) {
  return new Promise((resolve) => {
    const im = new Image()
    im.onload = () => {
      const c = document.createElement('canvas')
      c.width = im.naturalWidth
      c.height = im.naturalHeight
      c.getContext('2d').drawImage(im, 0, 0)
      resolve({ url: c.toDataURL('image/jpeg', quality), ar: im.naturalWidth / im.naturalHeight || 1 })
    }
    im.onerror = () => resolve({ url: dataUrl, ar: 1 })
    im.src = dataUrl
  })
}

/**
 * The ink box of a render, as FRACTIONS of the image: {x, y, w, h}. The measurement behind
 * `cropToContent`, exported so several renders can share one box (the comparison page crops
 * its six cells to the union of their boxes, keeping one scale). Null when nothing is found.
 */
export async function contentBox(dataUrl, opts = {}) {
  const im = await loadImg(dataUrl)
  if (!im) return null
  return boxOfImage(im, opts).box
}

/** The ink box of a decoded image, and the canvas it was read from (so a caller can crop it). */
function boxOfImage(im, { minDiff = 30 } = {}) {
  const W = im.naturalWidth, H = im.naturalHeight
  const c = document.createElement('canvas'); c.width = W; c.height = H
  const ctx = c.getContext('2d'); ctx.drawImage(im, 0, 0)
  const d = ctx.getImageData(0, 0, W, H).data
  const bg = [d[0], d[1], d[2]] // top-left corner = background
  const rowC = new Uint32Array(H), colC = new Uint32Array(W)
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4
      // `minDiff`: how far from the corner colour a pixel must be to count as ink. 30 suits the
      // SSE report's renders; a case-report render on pure white lights bone up to ~(250,245,235),
      // 20 from white, so those callers pass a lower value or the dome top and the occiput —
      // the brightest bone — are trimmed off the box (seen on the trial case, 28 Sep).
      if (d[i + 3] > 128 &&
          Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2]) > minDiff) {
        rowC[y]++; colC[x]++
      }
    }
  }
  const span = (arr) => {
    let mx = 0; for (const v of arr) if (v > mx) mx = v
    const t = Math.max(3, mx * 0.03)
    let lo = 0, hi = arr.length - 1
    while (lo < arr.length && arr[lo] < t) lo++
    while (hi >= 0 && arr[hi] < t) hi--
    return lo <= hi ? [lo, hi] : null
  }
  const cx = span(colC), cy = span(rowC)
  if (!cx || !cy) return { box: null, canvas: c }
  const padX = Math.round((cx[1] - cx[0]) * 0.03), padY = Math.round((cy[1] - cy[0]) * 0.03)
  const x0 = Math.max(0, cx[0] - padX), x1 = Math.min(W - 1, cx[1] + padX)
  const y0 = Math.max(0, cy[0] - padY), y1 = Math.min(H - 1, cy[1] + padY)
  return { box: { x: x0 / W, y: y0 / H, w: (x1 - x0 + 1) / W, h: (y1 - y0 + 1) / H }, canvas: c }
}

/**
 * Crop a render to the TIGHT bounding box of its visible model, so the model
 * fills its box regardless of stray or asymmetric geometry inflating the
 * render's framing (team feedback: "model too small in the box").
 *
 * Content = pixels differing from the corner background colour, which handles a
 * white OR black background. The per-row/column threshold is **relative to the
 * peak** coverage on that axis, so a thin noise column (< 3% of the model's peak
 * width) is trimmed while real thin structures survive; an absolute threshold
 * missed a vertical noise spike in testing.
 *
 * @returns {Promise<{url: string, ar: number}>} cropped PNG + aspect ratio
 */
export async function cropToContent(dataUrl, opts = {}) {
  const im = await loadImg(dataUrl)
  if (!im) return { url: dataUrl, ar: 1 }
  const W = im.naturalWidth, H = im.naturalHeight
  const { box, canvas: c } = boxOfImage(im, opts)   // one decode, one raster
  if (!box) return { url: dataUrl, ar: W / H }
  const x0 = Math.round(box.x * W), y0 = Math.round(box.y * H)
  const cw = Math.round(box.w * W), ch = Math.round(box.h * H)
  const oc = document.createElement('canvas'); oc.width = cw; oc.height = ch
  oc.getContext('2d').drawImage(c, x0, y0, cw, ch, 0, 0, cw, ch)
  return { url: oc.toDataURL('image/png'), ar: cw / ch }
}

/**
 * Where a contain-fitted picture actually lands inside its box, centred.
 *
 * The stamper and the draw MUST agree to the millimetre — a stamped tile placed
 * from a different rectangle lands off the page's lattice — so the case report
 * takes both from here. sseReport.js and illustrationDoc.js still carry their
 * own copies of the same arithmetic; moving them would move two shipped
 * documents' bytes, so they are left alone deliberately.
 */
export function containFit({ x, y, w, h }, ar) {
  let dw = w, dh = w / ar
  if (dh > h) { dh = h; dw = h * ar }
  return { x: x + (w - dw) / 2, y: y + (h - dh) / 2, w: dw, h: dh }
}

let CANVAS_FACE = null            // the family name, once loaded

/**
 * Can this environment draw text on a canvas at all?
 *
 * Node under the golden-test stubs HAS a `document` — the stubs install one — so
 * the probe is for the font-loading API, not for `document`. Probing the wrong
 * one made every golden build report a fault it did not have: 30 warnings on a
 * 14-page report, one per picture.
 */
const canStampText = () => (
  typeof document !== 'undefined' && typeof FontFace !== 'undefined' && !!document.fonts
)

/**
 * Register the house face for CANVAS text, from the same TTF `registerMulish`
 * embeds in the PDF — so a glyph stamped into a picture has the outline the
 * vector lattice draws beside it.
 *
 * Returns null where the environment cannot draw canvas text at all (node, under
 * the golden-test stubs) or where the face itself failed. A failure is not
 * memoised, so a transient one does not disable the stamp for the session.
 */
export async function canvasMulish() {
  if (CANVAS_FACE) return CANVAS_FACE
  if (!canStampText()) return null
  try {
    const res = await fetch('/fonts/Mulish-Regular.ttf')
    const face = new FontFace('Osteo3dMulish', await res.arrayBuffer())
    await face.load()
    document.fonts.add(face)
    CANVAS_FACE = 'Osteo3dMulish'
    return CANVAS_FACE
  } catch { return null }
}

/**
 * Stamp the page's watermark lattice into ONE picture's own pixels, at the true
 * opacity — the watermark the picture used to get from a transparent object
 * drawn over it. See `overWhite` for why the PDF may no longer carry one.
 *
 * `cell` is the BOX this picture is fitted into, in page millimetres; the
 * rectangle it actually lands in comes back as `fit`, from the one `containFit`
 * the writer also draws with. The canvas is transformed into PAGE millimetres
 * and the same `watermarkLattice` is walked, so a tile straddling the picture's
 * edge is drawn from the same ANCHOR on both sides of it. The geometry is exact,
 * not a tolerance. The colour is not quite: inside the picture the tile is a
 * q0.85 JPEG, so against the vector tile beside it the luminance matches to 0.5
 * of a level and the chroma to 3 (Chrome subsamples it) — measured by
 * tools/case-report-e2e/watermark-stamp.mjs, on a 17 %-deep grey.
 *
 * ONE decode and ONE encode: the picture is 1600 px square and this runs inside
 * the per-op loop, so it hands back the JPEG the writer embeds rather than a
 * full-size PNG for someone else to re-decode.
 *
 * @param {object}  [opts]
 * @param {string}  [opts.font]     the family the DOCUMENT resolved to. The tiles
 *   on the page are set in it, so the stamp has to be too or a tile crossing a
 *   picture's edge changes typeface halfway.
 * @returns {Promise<{url: string, ar: number, fit: object, stamped: boolean, reason?: string}>}
 *   `reason` is set wherever a browser COULD have stamped and did not, so the
 *   caller can say so out loud. Node reports none — it is not a fault there.
 */
export async function stampWatermark(dataUrl, cell, w, { font = 'Mulish', quality = 0.85 } = {}) {
  const im = await loadImg(dataUrl)
  const ar = im ? (im.naturalWidth / im.naturalHeight || 1) : 1
  const fit = containFit(cell, ar)
  const give = (extra) => ({ url: dataUrl, ar, fit, stamped: false, ...extra })
  if (!im) return give({ reason: 'the picture could not be read back' })
  // No watermark asked for, or an environment with no canvas text (node, under
  // the golden-test stubs): nothing to report. A FAILURE to load the face, or a
  // picture that cannot be read back, is a fault and says so.
  if (!w?.text || !canStampText()) return give({})
  // Mulish for a document set in Mulish; whatever the document fell back to
  // otherwise, so the two halves of a tile still match.
  const family = font === 'Mulish' ? await canvasMulish() : `${font}, sans-serif`
  if (!family) return give({ reason: 'the house font did not load' })

  const c = document.createElement('canvas')
  c.width = im.naturalWidth
  c.height = im.naturalHeight
  const ctx = c.getContext('2d')
  ctx.drawImage(im, 0, 0)

  const sizeMm = (w.size ?? 13) * PT_MM
  // jsPDF rotates text counter-clockwise about the anchor; canvas rotates
  // clockwise for a positive angle.
  const angle = -(w.angle ?? 30) * Math.PI / 180
  // A tile anchored outside the picture still paints into it: the word runs up
  // to its own length away from its anchor. One em per character is an upper
  // bound on that reach, so no tile that could touch these pixels is skipped.
  const reach = sizeMm * (w.text.length + 1)

  ctx.save()
  ctx.scale(c.width / fit.w, c.width / fit.w)  // 1 unit = 1 mm of page; uniform, or the tiles skew
  ctx.translate(-fit.x, -fit.y)                // ... on the page's own origin
  ctx.globalAlpha = w.opacity ?? 0.09
  ctx.fillStyle = `rgb(${(w.color ?? [140, 150, 155]).join(',')})`
  ctx.font = `${sizeMm}px "${family}"`
  ctx.textBaseline = 'alphabetic'
  for (const { x, y } of watermarkLattice(w)) {
    if (x < fit.x - reach || x > fit.x + fit.w + reach) continue
    if (y < fit.y - reach || y > fit.y + fit.h + reach) continue
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(angle)
    ctx.fillText(w.text, 0, 0)
    ctx.restore()
  }
  ctx.restore()
  return { url: c.toDataURL('image/jpeg', quality), ar, fit, stamped: true }
}

